// Панель настроек голоса: скорость речи (бегунок), движок озвучки (TTS),
// распознавание речи (STT), фоновый звук «думает…». Открывается шестерёнкой в шапке.

import { useEffect, useRef, useState } from 'react'
import {
  AMBIENT_OPTIONS,
  getAmbient,
  getSpeed,
  getStt,
  getTtsEngine,
  setAmbient,
  setSpeed,
  setStt,
  setTtsEngine,
  type AmbientId,
  type SttEngine,
  type TtsEngine,
} from '../net/settings'
import { ThinkingSound } from '../audio/ThinkingSound'

const TTS_OPTIONS: Array<{ key: TtsEngine; label: string; hint: string }> = [
  { key: 'piper', label: 'Piper', hint: 'разные голоса у агентов, бесплатно' },
  { key: 'edge', label: 'Microsoft', hint: 'один нейроголос, бесплатно' },
  { key: 'openai', label: 'OpenAI', hint: 'как в Telegram (onyx), платно' },
  { key: 'browser', label: 'Телефон', hint: 'голос Siri/браузера, бесплатно' },
]

// Сколько играет предпрослушивание фонового звука, прежде чем само затихнет.
const PREVIEW_MS = 6000

export function Settings({ onClose }: { onClose: () => void }) {
  const [speed, setSpeedState] = useState(getSpeed())
  const [engine, setEngineState] = useState<TtsEngine>(getTtsEngine())
  const [stt, setSttState] = useState<SttEngine>(getStt())
  const [ambient, setAmbientState] = useState<AmbientId>(getAmbient())
  const [playing, setPlaying] = useState<AmbientId | null>(null)

  const previewRef = useRef<ThinkingSound | null>(null)
  const timerRef = useRef<number | undefined>(undefined)

  // Останавливаем и закрываем предпрослушивание при закрытии панели.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      previewRef.current?.close()
      previewRef.current = null
    }
  }, [])

  const stopPreview = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = undefined
    previewRef.current?.stop()
    setPlaying(null)
  }

  const preview = async (id: AmbientId) => {
    if (playing === id) {
      stopPreview()
      return
    }
    if (timerRef.current) clearTimeout(timerRef.current)
    if (!previewRef.current) previewRef.current = new ThinkingSound(id)
    const snd = previewRef.current
    snd.stop()
    await snd.unlock()
    snd.setVariant(id)
    snd.start()
    setPlaying(id)
    timerRef.current = window.setTimeout(() => stopPreview(), PREVIEW_MS)
  }

  const changeAmbient = (id: AmbientId) => {
    setAmbient(id)
    setAmbientState(id)
    void preview(id) // выбрал → сразу слышно
  }

  const changeSpeed = (v: number) => {
    setSpeed(v)
    setSpeedState(v)
  }
  const changeEngine = (e: TtsEngine) => {
    setTtsEngine(e)
    setEngineState(e)
  }
  const changeStt = (e: SttEngine) => {
    setStt(e)
    setSttState(e)
  }

  const close = () => {
    stopPreview()
    onClose()
  }

  return (
    <div className="sheet" onClick={close}>
      <div className="sheet__card" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__grab" />
        <div className="sheet__head">
          <h2 className="sheet__title">Настройки голоса</h2>
          <button className="sheet__close" onClick={close} aria-label="Закрыть">✕</button>
        </div>

        <div className="sheet__body">
        <div className="setrow">
          <div className="setrow__label">
            Скорость речи <span className="setrow__val">{speed.toFixed(2)}×</span>
          </div>
          <input
            className="setrow__range"
            type="range"
            min={0.7}
            max={1.6}
            step={0.05}
            value={speed}
            onChange={(e) => changeSpeed(Number(e.target.value))}
          />
          <div className="setrow__ticks"><span>медленно</span><span>быстро</span></div>
        </div>

        <div className="setrow">
          <div className="setrow__label">Озвучка (TTS)</div>
          <div className="seg">
            {TTS_OPTIONS.map((o) => (
              <button
                key={o.key}
                className={`seg__btn ${engine === o.key ? 'seg__btn--active' : ''}`}
                onClick={() => changeEngine(o.key)}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div className="setrow__hint">{TTS_OPTIONS.find((o) => o.key === engine)?.hint}</div>
        </div>

        <div className="setrow">
          <div className="setrow__label">Распознавание речи (STT)</div>
          <div className="seg">
            <button className={`seg__btn ${stt === 'browser' ? 'seg__btn--active' : ''}`} onClick={() => changeStt('browser')}>
              Телефон (Siri)
            </button>
            <button className={`seg__btn ${stt === 'whisper' ? 'seg__btn--active' : ''}`} onClick={() => changeStt('whisper')}>
              Whisper (сервер)
            </button>
          </div>
          <div className="setrow__hint">
            {stt === 'whisper'
              ? 'Запись отправляется на сервер, распознаёт Whisper (точнее, но с задержкой ~5 сек).'
              : 'Быстро, встроено в телефон (без знаков препинания).'}
          </div>
        </div>

        <div className="setrow">
          <div className="setrow__label">Звук, когда агент думает</div>
          <div className="amb">
            {AMBIENT_OPTIONS.map((o) => {
              const active = ambient === o.key
              const on = playing === o.key
              return (
                <div
                  key={o.key}
                  className={`amb__row ${active ? 'amb__row--active' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => changeAmbient(o.key)}
                >
                  <div className="amb__txt">
                    <span className="amb__name">
                      {o.label}
                      {active && <span className="amb__check">✓ выбрано</span>}
                    </span>
                    <span className="amb__hint">{o.hint}</span>
                  </div>
                  <button
                    className={`amb__play ${on ? 'amb__play--on' : ''}`}
                    aria-label={on ? `Остановить: ${o.label}` : `Прослушать: ${o.label}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      void preview(o.key)
                    }}
                  >
                    {on ? '◼' : '▶'}
                  </button>
                </div>
              )
            })}
          </div>
          <div className="setrow__hint">Нажми вариант, чтобы выбрать и услышать. По умолчанию — «Пульс».</div>
        </div>

          <button className="sheet__done" onClick={close}>Готово</button>
        </div>
      </div>
    </div>
  )
}
